/** The panels of every non-results state (UX-SPEC §4.1, §4.4, §4.5). Each state shows text, never a blank page. */
import { useEffect, useRef, useState } from 'preact/hooks';
import { STAGES } from '../../shared/protocol.ts';
import { cancel, discardInterrupted, rerun, run } from '../controller.ts';
import { overranPrior } from '../estimate.ts';
import { fmtElapsed, fmtInt } from '../format.ts';
import { analysisPriorMs } from '../rates.ts';
import {
	type AppError,
	type Phase,
	error,
	fetchHost,
	file,
	intakeInfo,
	phase,
	progress,
	runStartedAt,
	setPhase,
	sourceName,
	storedJob,
	tab,
} from '../state.ts';
import { INTAKE, STATE_TEXT as T, WORKING } from '../strings.ts';
import { Notice } from '../ui.tsx';
import { Spinner, useEstimate } from './Working.tsx';

function useElapsed(since: number): string {
	const [now, setNow] = useState(performance.now());
	useEffect(() => {
		const t = window.setInterval(() => setNow(performance.now()), 1000);
		return () => window.clearInterval(t);
	}, []);
	return fmtElapsed(now - since);
}

export function headingFor(p: Phase): string {
	switch (p) {
		case 'reading':
			return T.reading(sourceName.value);
		case 'fetching':
			return T.fetching(fetchHost.value);
		case 'restoring':
			return T.restoring(sourceName.value);
		case 'rendering':
			return T.rendering;
		case 'intake-checked': {
			const i = intakeInfo.value;
			return i
				? T.intakeChecked(
						fmtInt(i.words),
						INTAKE.formatNames[
							i.dispatchedFormat ?? (i.format === 'url' || i.format === 'paste' ? 'txt' : i.format)
						]
					)
				: T.parsing;
		}
		case 'analysing':
			return T.analysing;
		default:
			return T.parsing;
	}
}

const CANCELABLE: ReadonlySet<Phase> = new Set([
	'fetching',
	'parsing',
	'intake-checked',
	'analysing',
]);

export function ProgressPanel() {
	const p = phase.value;
	const prog = progress.value;
	const info = intakeInfo.value;
	const elapsed = useElapsed(runStartedAt.value);
	const hasBar = !(p === 'reading' || p === 'fetching' || p === 'restoring' || p === 'rendering');
	// the estimate: a prior from the word count once the intake has it, then the bar's own fraction (UD-11)
	const priorMs = hasBar && info ? analysisPriorMs(info.words) : null;
	const { now, estimate } = useEstimate({
		startedAt: runStartedAt.value,
		fraction: hasBar && prog.pct > 0 ? prog.pct / 100 : null,
		priorMs,
	});
	const cancelRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		cancelRef.current?.focus();
	}, []);
	const stageIndex = STAGES.findIndex((s) => s.key === prog.stage);
	const label = T.stage[prog.stage];
	const detail =
		prog.detail && prog.stage === 'read'
			? ` (${T.page(fmtInt(prog.detail.page), fmtInt(prog.detail.pages))})`
			: '';
	return (
		<section class="panel progress" aria-labelledby="progress-h">
			<h1 id="progress-h">{headingFor(p)}</h1>
			<dl class="facts-inline">
				<div>
					<dt class="visually-hidden">Document</dt>
					<dd class="docname-lg">{sourceName.value}</dd>
				</div>
				{info ? (
					<div>
						<dt class="visually-hidden">Words</dt>
						<dd data-testid="intake-words">{INTAKE.pasteWords(fmtInt(info.words))}</dd>
					</div>
				) : null}
			</dl>
			{!hasBar ? (
				<p class="progress-working" data-testid="progress-spinner" aria-busy="true">
					<Spinner />
					<span>{T.waitingFor}</span>
				</p>
			) : (
				<>
					{/* biome-ignore lint/a11y/useFocusableInteractive: a progressbar is not interactive */}
					<div
						class="bar"
						role="progressbar"
						aria-label={T.progressLabel}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={Math.round(prog.pct)}
					>
						<div class="bar-fill" ref={(el) => el?.style.setProperty('width', `${prog.pct}%`)} />
					</div>
					<ol class="stages">
						{STAGES.map((s, i) => (
							<li
								key={s.key}
								class={i < stageIndex ? 'is-done' : i === stageIndex ? 'is-now' : ''}
								aria-current={i === stageIndex ? 'step' : undefined}
							>
								{T.stage[s.key]}
								{i === stageIndex ? detail : ''}
							</li>
						))}
					</ol>
					<div class="visually-hidden" aria-live="polite" data-testid="stage-live">
						{T.stageAnnounce(label)}
					</div>
				</>
			)}
			<p class="dim" data-testid="elapsed">
				{T.elapsed(elapsed)}
			</p>
			{estimate ? (
				<p data-testid="estimate">{estimate.text}</p>
			) : overranPrior(now - runStartedAt.value, priorMs) ? (
				<p data-testid="estimate-longer">{WORKING.longerThanExpected}</p>
			) : null}
			{CANCELABLE.has(p) ? (
				<button type="button" class="btn btn-secondary" ref={cancelRef} onClick={cancel}>
					{T.cancel}
				</button>
			) : null}
		</section>
	);
}

function RetryLabel({ err }: { err: AppError }) {
	const [left, setLeft] = useState(() =>
		err.retryAfter ? Math.max(0, err.retryAfter - Math.floor((Date.now() - err.at) / 1000)) : 0
	);
	useEffect(() => {
		if (!err.retryAfter) return;
		const t = window.setInterval(
			() => setLeft(Math.max(0, (err.retryAfter ?? 0) - Math.floor((Date.now() - err.at) / 1000))),
			500
		);
		return () => window.clearInterval(t);
	}, [err]);
	return (
		<button type="button" class="btn btn-primary" disabled={left > 0} onClick={() => void run()}>
			{left > 0 ? `${T.tryAgain} (${left})` : T.tryAgain}
		</button>
	);
}

/** An error or a limit message above the intake, with the one action the table of §4.5 names. */
export function ErrorPanel({ label }: { label?: string }) {
	const err = error.value;
	if (!err) return null;
	const a = err.action;
	return (
		<div class="status-stack">
			<Notice kind="error">
				<span data-testid="error-message">{err.message}</span>
				{err.detail ? <div class="dim">{err.detail}</div> : null}
			</Notice>
			<div class="panel-actions">
				{a === 'retry' ? (
					<RetryLabel err={err} />
				) : a === 'run-again' ? (
					<button
						type="button"
						class="btn btn-primary"
						onClick={() => (storedJob.value ? rerun() : void run())}
					>
						{T.runAgain}
					</button>
				) : a === 'edit-url' ? (
					<button
						type="button"
						class="btn btn-secondary"
						onClick={() => {
							tab.value = 'url';
							setPhase('empty');
							document.getElementById('url-box')?.focus();
						}}
					>
						{T.editUrl}
					</button>
				) : a === 'download' ? (
					<button
						type="button"
						class="btn btn-secondary"
						onClick={() => {
							tab.value = 'document';
							setPhase('empty');
						}}
					>
						{T.downloadInstead}
					</button>
				) : (
					<button
						type="button"
						class="btn btn-secondary"
						onClick={() => {
							file.value = null;
							tab.value = 'document';
							setPhase('empty');
						}}
					>
						{T.chooseAnother}
					</button>
				)}
			</div>
			{label ? <span class="visually-hidden">{label}</span> : null}
		</div>
	);
}

export function CancelledNotice() {
	return (
		<div class="status-stack">
			<Notice kind="info" live>
				<span data-testid="cancelled-message">{T.cancelled}</span>
			</Notice>
			<div class="panel-actions">
				<button type="button" class="btn btn-primary" onClick={() => void run()}>
					{T.runAgain}
				</button>
			</div>
		</div>
	);
}

export function InterruptedPanel() {
	const job = storedJob.value;
	return (
		<section class="panel" aria-labelledby="int-h">
			<h1 id="int-h">{T.interrupted(job?.name ?? sourceName.value)}</h1>
			<div class="panel-actions">
				<button type="button" class="btn btn-primary" onClick={() => rerun()}>
					{T.runAgain}
				</button>
				<button type="button" class="btn btn-secondary" onClick={discardInterrupted}>
					{T.discard}
				</button>
			</div>
		</section>
	);
}
